// VOY-417 §7 — What Voy remembered recently — memory feed.
//
// Timestamped observations from `user_agent_profile.memory_feed`
// (VOY-503 schema): observation, detected_at, source_event.
//
// **Per-entry "Forget" wires to VOY-458's backend** — per the ticket,
// no parallel forget endpoint.
//
// STALE-SPEC DEVIATION (flagged in PR): VOY-458's `/memory/delete` is
// dimension-keyed (PROFILE_DIMENSIONS), but memory_feed entries are
// untyped free-text observations without a dimension. There's no
// existing backend endpoint that accepts an observation index/text +
// tombstones it. We render the Forget button on every row with a
// wire-point disabled state. Backend follow-up: either add an
// observation-index forget endpoint OR derive a dimension client-side
// per entry (currently impossible without semantic analysis).

import { Clock, MessageSquare, Search, Calendar, Sparkles, Settings, BarChart3 } from 'lucide-react'
import type { AgentProfile, MemoryFeedEntry, MemoryFeedSource } from '@/types/profile'

export interface MemoryFeedSectionProps {
  agentProfile: AgentProfile | null
  loading: boolean
}

function iconForSource(s: MemoryFeedSource) {
  switch (s) {
    case 'chat': return <MessageSquare aria-hidden="true" />
    case 'search': return <Search aria-hidden="true" />
    case 'booking': return <Calendar aria-hidden="true" />
    case 'quiz': return <Sparkles aria-hidden="true" />
    case 'preference': return <Settings aria-hidden="true" />
    case 'analysis': return <BarChart3 aria-hidden="true" />
    default: return <Clock aria-hidden="true" />
  }
}

function sourceLabel(s: MemoryFeedSource): string {
  switch (s) {
    case 'chat': return 'CHAT'
    case 'search': return 'SEARCH'
    case 'booking': return 'BOOKING'
    case 'quiz': return 'QUIZ'
    case 'preference': return 'PREFERENCE'
    case 'analysis': return 'ANALYSIS'
    default: return 'EVENT'
  }
}

function relativeTime(iso: string): string {
  try {
    const ms = Date.now() - new Date(iso).getTime()
    if (ms < 60 * 1000) return 'just now'
    if (ms < 60 * 60 * 1000) return `${Math.floor(ms / (60 * 1000))}m ago`
    if (ms < 24 * 60 * 60 * 1000) return `${Math.floor(ms / (60 * 60 * 1000))}h ago`
    const days = Math.floor(ms / (24 * 60 * 60 * 1000))
    if (days < 30) return `${days}d ago`
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

export function MemoryFeedSection({ agentProfile, loading }: MemoryFeedSectionProps) {
  const feed: MemoryFeedEntry[] = agentProfile?.memory_feed ?? []
  const isEmpty = !loading && feed.length === 0

  return (
    <section className="card" aria-labelledby="feed-h">
      <div className="card-h">
        <h3 id="feed-h">What Voy remembered recently</h3>
        <span className="meta">{loading ? 'LOADING…' : 'LIVE FEED'}</span>
      </div>

      {isEmpty ? (
        <div className="you-empty-state">
          <p className="you-empty-headline">No observations yet.</p>
          <p className="you-empty-sub">
            As you chat, search, and book, Voy posts what it&apos;s learning here — with timestamps.
          </p>
        </div>
      ) : (
        <ul className="you-feed">
          {feed.map((entry, i) => (
            <li key={`${entry.detected_at}-${i}`} className="you-feed-entry">
              <span className={`you-feed-icon source-${entry.source_event}`}>
                {iconForSource(entry.source_event)}
              </span>
              <div className="you-feed-body">
                <p className="you-feed-observation">{entry.observation}</p>
                <div className="you-feed-meta">
                  <span className="lbl">{sourceLabel(entry.source_event)}</span>
                  <span className="you-feed-time">{relativeTime(entry.detected_at)}</span>
                </div>
              </div>
              <button
                type="button"
                className="btn-ghost you-feed-forget"
                style={{ padding: '4px 10px', fontSize: 11, opacity: 0.5 }}
                disabled
                title="Backend coverage pending — VOY-458 needs an untyped-observation forget endpoint. Use the Memory Editor below to delete typed facts."
              >
                Forget
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
